import React, { useEffect, useRef, useState } from 'react';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import styles from './resource-list-grid.module.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';
import { Chevron } from '../icon/chevron';
import { A11y, Navigation, Pagination, Scrollbar } from 'swiper/modules';
import { ResourceGrid as ResourceGridProp, Taxonomy } from '../../typescript/component';
import Button from '../ui/button';
import BlogCard from '../cards/blog-card';
import { handleSwiperBreakpoint } from '../../sdk/utils';
import { generateRandomId } from '../../helper/utils';
import { useRouter } from 'next/router';
export default function ResourceListGrid({
  resource_grid,
}: {
  resource_grid: ResourceGridProp;
  taxonomies?: Taxonomy[];
}) {
  const { locale } = useRouter();
  const {
    eyebrow,
    display_eyebrow,
    title,
    description,
    cta,
    display_cta,
    featured_resources,
    component_type,
  } = resource_grid;
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const [paginationId] = useState(generateRandomId());

  const paginationRef = useRef(null);
  const totalSlides = featured_resources.length;
  const [currentSlidesPerView, setCurrentSlidesPerView] = useState<number>(3);
  const shouldShowNavigation = currentSlidesPerView < totalSlides && totalSlides > 1;

  const [currentLanguage, setCurrentLanguage] = useState(locale);
  const [keyboardUser, setKeyboardUser] = useState(false);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.params) {
      if (typeof swiperRef.current.params.navigation === 'object') {
        swiperRef.current.params.navigation.prevEl = prevRef.current;
        swiperRef.current.params.navigation.nextEl = nextRef.current;
        swiperRef.current.navigation.destroy();
        swiperRef.current.navigation.init();
        swiperRef.current.navigation.update();
      }

      if (typeof swiperRef.current.params.pagination === 'object') {
        swiperRef.current.params.pagination.el = paginationRef.current;
        swiperRef.current.pagination.destroy();
        swiperRef.current.pagination.init();
        swiperRef.current.pagination.update();
      }
    }
  }, [shouldShowNavigation, swiperRef.current]);

  // Reset keyboardUser when focus leaves the Swiper
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !swiperRef.current?.el.contains(relatedTarget)) {
      const swiper = swiperRef.current;

      setKeyboardUser(false);

      if (swiper)
        swiper.navigation.update();
    }
  };

  return (
    <section className={`${styles.container} ${styles.resourceGridContainer}`}>
      <div className={styles.content}>
        {eyebrow && display_eyebrow && <span className={styles.eyeBrow}>{eyebrow}</span>}
        {title && <h3 className={styles.resourceCarouselTitle}>{title}</h3>}
        {description && <div className={styles.sentencesAboutCompany}>{description}</div>}
        {cta && display_cta && cta.title && (cta.url ?? cta.href) && (
          <Button
            text={cta.title}
            showIcon
            propWidth="250px"
            propBackgroundColor="var(--brand-primary-500)"
            propHoverBackgroundColor="var(--white)"
            propTextColor="var(--white)"
            propHoverText="var(--brand-primary-500)"
            link={cta.url ?? cta.href}
          />
        )}
      </div>
      <div
        className={`swiper-grid ${styles.grid}`}
        style={{ position: 'relative' }}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            setKeyboardUser(true);
          }
        }}
        onBlur={handleBlur}
        onMouseDown={() => {
          const swiper = swiperRef.current;

          setKeyboardUser(false);

          if (swiper)
            swiper.navigation.update();
        }} // reset on mouse interaction
      >
        <Swiper
          modules={[Navigation, Pagination, Scrollbar, A11y]}
          loop={!keyboardUser} // disable loop for keyboard users
          onSwiper={(swiper: SwiperType) => (swiperRef.current = swiper)}
          onBreakpoint={(swiper: SwiperType) =>
            handleSwiperBreakpoint(swiper, setCurrentSlidesPerView)
          }
          spaceBetween={1}
          centeredSlides={false}
          navigation={{
            nextEl: prevRef.current,
            prevEl: nextRef.current,
          }}
          pagination={{
            el: `#${paginationId}`,
            enabled: shouldShowNavigation,
            dynamicBullets: true,
          }}
          breakpoints={{
            320: {
              // Mobile screens
              slidesPerView: 1.4,
            },
            768: {
              // Tablets
              slidesPerView: 2.4,
            },
            1024: {
              // Tablets
              slidesPerView: 1.8,
            },
            1200: {
              // Desktop
              slidesPerView: 3,
            },
          }}
          className={`${styles.slider} ${component_type.toLowerCase().replace(' ', '-')}`}
        >
          {featured_resources.length > 0 &&
            featured_resources.map((item, index) => (
              <SwiperSlide key={index} className={styles.slide}>
                <BlogCard
                  key={`BlogCard-${currentLanguage}`}
                  blog_post={item}
                  component_type={component_type}
                />
              </SwiperSlide>
            ))}
        </Swiper>
        {shouldShowNavigation && (
          <div className={styles.navigationWrapper}>
            <Chevron ref={prevRef} direction="left" variant="dark" />
            <div ref={paginationRef}></div>
            <Chevron ref={nextRef} direction="right" variant="dark" />
          </div>
        )}
      </div>
    </section>
  );
}
